import {useQuery} from 'react-query'

import {
  api,
  queryClient,
  useMutationQuery,
  replaceQueryClientDataItem,
} from 'base'
import {showSuccessNotification} from 'common/helpers'
import {IDocument, TDocumentsModel} from '.'

interface IUseAddDocumentParams {
  modelUuid: string
  modelType: TDocumentsModel
  projectUuid?: string
  collection: string
}

export const useGetDocuments = (projectUuid: string) =>
  useQuery<IDocument[]>(['documents', projectUuid], async () => {
    const {data} = await api.get(`/project/${projectUuid}/files`)
    return data.data
  })

export const useGetChatFiles = (projectUuid: string, enabled: boolean = true) =>
  useQuery<IDocument[]>(
    ['chatFiles', projectUuid],
    async () => {
      const {data} = await api.get(`/chat/project/${projectUuid}/files`)
      return data.data.map((item: any) => item.attachment)
    },
    {enabled}
  )

export const useAddDocuments = ({
  modelUuid,
  modelType,
  projectUuid,
  collection,
}: IUseAddDocumentParams) =>
  useMutationQuery<IDocument[], any, string[]>(
    ['addDocument', modelUuid, collection],
    async (filesUuids) => {
      const {data} = await api.post('/media/add', {
        collection,
        files: filesUuids,
        model: {
          uuid: modelUuid,
          type: modelType,
        },
      })
      return data.data
    },
    {
      onSuccess(data) {
        const isAml = collection !== 'amlCheck' && modelType === 'amlCheck'
        const requestKey = isAml
          ? ['aml', projectUuid]
          : ['documents', projectUuid ?? modelUuid]
        queryClient.setQueriesData(requestKey, (oldData: any[]) => {
          if (isAml) {
            const newData = oldData.slice()
            const currentAmlIndex = newData.findIndex(
              (aml) => aml.uuid === modelUuid
            )
            const currentAml = newData[currentAmlIndex]
            newData[currentAmlIndex] = {
              ...currentAml,
              other: [...data, ...currentAml.other],
            }
            return newData
          }
          return [...oldData, ...data]
        })
        queryClient.invalidateQueries(requestKey)
      },
    }
  )

export const useCopyDocument = (
  documentUuid: string,
  projectUuid: string,
  isChatDocument?: boolean
) =>
  useMutationQuery<IDocument, any, any>(
    ['copyDocument', documentUuid],
    async ({key, keyPath}) => {
      const isCopyToAmlCheck = key !== 'aml' && keyPath[1] === 'amlDocuments'
      const [collection, drawdownUuid] = key.split('/')
      const requestData: any = {
        collection: isCopyToAmlCheck ? 'other' : collection,
      }
      if (isCopyToAmlCheck) {
        requestData.model = {
          uuid: key,
          type: 'amlCheck',
        }
      } else if (drawdownUuid) {
        requestData.model = {
          uuid: drawdownUuid,
          type: 'drawdown',
        }
      } else if (isChatDocument) {
        requestData.model = {
          uuid: projectUuid,
          type: 'project',
        }
      }
      const {data} = await api.post(`/media/${documentUuid}/copy`, requestData)
      return data.data
    },
    {
      onSuccess(data) {
        if (data.collection === 'other') {
          queryClient.invalidateQueries(['aml', projectUuid])
        } else {
          const requestKey = ['documents', projectUuid]
          queryClient.setQueriesData(requestKey, (oldData: IDocument[]) => [
            ...oldData,
            data,
          ])
          queryClient.invalidateQueries(requestKey)
        }
        showSuccessNotification(`Document "${data.name}" has been copied.`)
      },
    }
  )

export const useRenameDocument = (
  documentUuid: string,
  projectUuid: string,
  onClose: () => void
) =>
  useMutationQuery<IDocument, any, any>(
    ['renameDocument', documentUuid],
    async (values) => {
      const {data} = await api.post(`/media/${documentUuid}/rename`, values)
      return data.data
    },
    {
      onSuccess(data) {
        if (data.collection === 'other') {
          queryClient.invalidateQueries(['aml', projectUuid])
        } else {
          const requestKey = ['documents', projectUuid]
          replaceQueryClientDataItem(requestKey, data)
          queryClient.invalidateQueries(requestKey)
        }
        onClose()
      },
    }
  )

export const useArchiveDocument = (documentUuid: string, projectUuid: string) =>
  useMutationQuery<IDocument, any, any>(
    ['archiveDocument', documentUuid],
    async () => {
      const {data} = await api.delete(`/media/${documentUuid}/archive`)
      return data.data
    },
    {
      onSuccess(data) {
        if (data.collection === 'other') {
          queryClient.invalidateQueries(['aml', projectUuid])
        } else {
          const requestKey = ['documents', projectUuid]
          replaceQueryClientDataItem(requestKey, data)
          queryClient.invalidateQueries(requestKey)
        }
      },
    }
  )
